import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, CircularProgress, Pagination, Alert, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { speakText } from '../../utils/speech';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const Dictionary: React.FC = () => {
  const navigate = useNavigate();
  const [words, setWords] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 24;

  const fetchWords = async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=dictionary&page=${page}&limit=${limit}`;
      
      // If a letter is selected, search by starting letter
      if (selectedLetter) {
        queryStr += `&search=${encodeURIComponent('^' + selectedLetter)}`;
      } else if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setWords(res.data.data);
        setTotalPages(res.data.pagination.pages || 1);
      } else {
        setError('Failed to fetch dictionary entries.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to dictionary API');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWords();
  }, [page, selectedLetter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSelectedLetter(null); // Clear letter filter on search submit
    setPage(1);
    fetchWords();
  };

  const handleLetterClick = (letter: string) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null); // Toggle off
    } else {
      setSelectedLetter(letter);
      setSearch(''); // Clear general search
    }
    setPage(1);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back navigation */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Academic Dictionary
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Expand your lexicon. Explore definitions, parts of speech, and sentence examples.
        </Typography>
      </Box>

      {/* Search Bar & A-Z Quick Jump */}
      <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <form onSubmit={handleSearchSubmit}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for any word..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
            <Button type="submit" variant="contained" size="large" sx={{ px: 4 }}>
              Search
            </Button>
          </Box>
        </form>

        {/* Alphabet quick jump */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
          {alphabet.map((letter) => (
            <Button
              key={letter}
              variant={selectedLetter === letter ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleLetterClick(letter)}
              sx={{ minWidth: 36, height: 36, p: 0 }}
            >
              {letter}
            </Button>
          ))}
          {selectedLetter && (
            <Button 
              variant="text" 
              color="error" 
              onClick={() => { setSelectedLetter(null); setPage(1); }}
              sx={{ ml: 1 }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      </Box>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Dictionary grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : words.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">No words found matching your query.</Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {words.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 3, 
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.04)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        pb: 0.5,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                          height: '6px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '999px'
                        }
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.light',
                          display: 'inline-block',
                          minWidth: '100%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.word}
                      </Typography>
                    </Box>
                    <IconButton
                      type="button"
                      aria-label={`Read aloud: ${item.word}`}
                      onClick={() => speakText([
                        item.word,
                        item.partOfSpeech,
                        item.definition,
                        item.synonyms?.length ? `Synonyms: ${item.synonyms.join(', ')}` : '',
                        item.antonyms?.length ? `Antonyms: ${item.antonyms.join(', ')}` : '',
                        ...(item.examples || []),
                      ].filter(Boolean).join('. '))}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      <VolumeUpIcon fontSize="small" />
                    </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                      {item.partOfSpeech || 'Part of speech not specified'}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.5 }}>
                      {item.definition}
                    </Typography>
                    {item.synonyms?.length > 0 && (
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        <strong>Synonyms:</strong> {item.synonyms.join(', ')}
                      </Typography>
                    )}
                    {item.antonyms?.length > 0 && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Antonyms:</strong> {item.antonyms.join(', ')}
                      </Typography>
                    )}
                    {item.examples && item.examples.length > 0 && (
                      <Box sx={{ pl: 2, borderLeft: '2px solid rgba(99, 102, 241, 0.4)' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          "{item.examples[0]}"
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(e, p) => setPage(p)} 
                color="primary" 
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Dictionary;
